import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";

const LINKS = [
  { id: "#work", label: "Work" },
  { id: "#services", label: "Menu" },
  { id: "#manifesto", label: "About" },
  { id: "#contact", label: "Contact" },
];

const Nav = () => {
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(id, { offset: -20 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.4 }}
        className="fixed top-0 left-0 z-50 w-full"
      >
        <div
          className={`mx-auto flex items-center justify-between px-6 md:px-12 lg:px-16 transition-[padding,background-color,backdrop-filter] duration-500 ${
            scrolled && !open
              ? "py-4 bg-[#f7f3ea]/70 backdrop-blur-xl border-b border-black/10"
              : "py-6 bg-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={go("#top")}
            className="flex items-baseline gap-2 group"
          >
            <span className="text-[15px] font-extrabold tracking-tight">
              STUDIO&nbsp;FRANCO
            </span>
          </a>

          {/* desktop links */}
          <nav className="hidden md:flex items-center gap-6 md:gap-9">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.id}
                onClick={go(l.id)}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="label sweep text-[#0a0a0a]/80 hover:text-[#0a0a0a]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* mobile hamburger */}
          <button
            type="button"
            data-testid="nav-hamburger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden relative z-[60] flex h-8 w-8 flex-col items-center justify-center gap-[6px]"
          >
            <motion.span
              animate={open ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3 }}
              className="block h-[2px] w-7 bg-[#0a0a0a]"
            />
            <motion.span
              animate={open ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3 }}
              className="block h-[2px] w-7 bg-[#0a0a0a]"
            />
          </button>
        </div>
      </motion.header>

      {/* mobile overlay menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: "-100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#f7f3ea] px-6 md:hidden"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.id}
                  onClick={go(l.id)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                  className="tight text-6xl font-extrabold text-[#0a0a0a]"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.a
              href="mailto:hello@studiofranco.co"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="label mt-16 text-[#0a0a0a]/60"
            >
              hello@studiofranco.co
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
